import { FeatureBox3D } from '../procedural/FeatureBox3D'
import { FeatureCylinder3D } from '../procedural/FeatureCylinder3D'
import { FeatureLabel3D } from '../procedural/FeatureLabel3D'
import { SurfaceFeature3D } from '../SurfaceFeature3D'
import { useSurfaceProps } from '@asset-viz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoShipDeckHatch,
  ShipDeckHatchType,
} from 'types'

/**
 * A component representing a hatch on the surface of a ship deck component
 * @param model             The hatch model (from the asset model)
 * @constructor
 */
export const DeckHatchFeature3D: React.FC<{
  model: GeckoShipDeckHatch
}> = ({ model }) => {
  //----------------------------------------------------------------------------

  const { bounds } = useSurfaceProps()
  const { width, height } = React.useMemo(
    () => ({ width: model.width, height: model.height }),
    [model.width, model.height]
  )

  const widthIsSmaller = React.useMemo(() => {
    return width.num <= height.num
  }, [width, height])

  /* Exist on all hatch types */
  const hingeWidth = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.04).num
    return height.scale(0.3).num
  }, [widthIsSmaller, width, height])

  const hingeHeight = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.3).num
    return height.scale(0.04).num
  }, [widthIsSmaller, width, height])

  const hinge1XPos = React.useMemo(() => {
    if (widthIsSmaller) return -width.scale(0.2).num
    return width.scale(0.5).num
  }, [widthIsSmaller, width])

  const hinge1YPos = React.useMemo(() => {
    if (widthIsSmaller) return height.scale(0.5).num
    return -height.scale(0.2).num
  }, [widthIsSmaller, height])

  const hinge2XPos = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.2).num
    return width.scale(0.5).num
  }, [widthIsSmaller, width])

  const hinge2YPos = React.useMemo(() => {
    if (widthIsSmaller) return height.scale(0.5).num
    return height.scale(0.2).num
  }, [widthIsSmaller, height])

  /* Exists on raised watertight hatches with scuttles */
  const raisedOuterWheelRadius = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.35).num
    return height.scale(0.35).num
  }, [widthIsSmaller, width, height])

  const raisedInnerWheelRadius = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.2).num
    return height.scale(0.2).num
  }, [widthIsSmaller, width, height])

  /* Exists on raised quick acting spring balanced hatches */
  const raisedSpringWheelRadius = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.275).num
    return height.scale(0.275).num
  }, [widthIsSmaller, width, height])

  const raisedSpringRectangle1Width = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.3).num
    return height.scale(0.15).num
  }, [widthIsSmaller, width, height])

  const raisedSpringRectangle1Height = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.15).num
    return height.scale(0.3).num
  }, [widthIsSmaller, width, height])

  const raisedSpringRectangle1XPos = React.useMemo(() => {
    if (widthIsSmaller) return 0
    return -width.scale(0.3).num
  }, [widthIsSmaller, width])

  const raisedSpringRectangle1YPos = React.useMemo(() => {
    if (widthIsSmaller) return -height.scale(0.3).num
    return 0
  }, [widthIsSmaller, height])

  const raisedSpringRectangle2Width = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.3).num
    return height.scale(0.05).num
  }, [widthIsSmaller, width, height])

  const raisedSpringRectangle2Height = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.05).num
    return height.scale(0.3).num
  }, [widthIsSmaller, width, height])

  const raisedSpringRectangle2XPos = React.useMemo(() => {
    if (widthIsSmaller) return 0
    return -width.scale(0.425).num
  }, [widthIsSmaller, width])

  const raisedSpringRectangle2YPos = React.useMemo(() => {
    if (widthIsSmaller) return -height.scale(0.425).num
    return 0
  }, [widthIsSmaller, height])

  /* Exists on flush watertight hatches */
  const flushCircleRadius = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.4).num
    return height.scale(0.4).num
  }, [widthIsSmaller, width, height])

  // const flushCircleThetaStart = widthIsSmaller
  //   ? (5 / 6) * Math.PI
  //   : (5 / 6) * Math.PI

  // const flushCircleThetaEnd = widthIsSmaller
  //   ? (13 / 6) * Math.PI
  //   : (13 / 6) * Math.PI

  const flushRectangleWidth = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.7).num
    return height.scale(0.2).num
  }, [widthIsSmaller, width, height])

  const flushRectangleHeight = React.useMemo(() => {
    if (widthIsSmaller) return width.scale(0.2).num
    return height.scale(0.7).num
  }, [widthIsSmaller, width, height])

  const flushRectangleXPos = React.useMemo(() => {
    if (widthIsSmaller) return 0
    return 0.3
  }, [widthIsSmaller])

  const flushRectangleYPos = React.useMemo(() => {
    if (widthIsSmaller) return 0.3
    return 0
  }, [widthIsSmaller])

  const { labelCoords, positionLabelLeft } = React.useMemo(() => {
    const positionLabelLeft =
      model.geometry.x.num < bounds.start.x.plus(bounds.end.x).scale(0.5).num

    const x = positionLabelLeft
      ? model.geometry.x.minus(width.scale(0.5))
      : model.geometry.x.plus(width.scale(0.5))

    const y = model.geometry.y
    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      x,
      y
    )
    return { labelCoords, positionLabelLeft }
  }, [model, bounds, width])

  //----------------------------------------------------------------------------

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelLeft ? 'right' : 'left'}
      />
      <SurfaceFeature3D model={model}>
        <FeatureBox3D
          feature={model}
          position={[0, 0, 0]}
          width={width.num}
          height={height.num}
          depth={1}
          debugColor={'pink'}
        />
        <FeatureBox3D
          feature={model}
          position={[hinge1XPos, 0, hinge1YPos]}
          width={hingeWidth}
          height={hingeHeight}
          depth={1.1}
          debugColor={'pink'}
        />
        <FeatureBox3D
          feature={model}
          position={[hinge2XPos, 0, hinge2YPos]}
          width={hingeWidth}
          height={hingeHeight}
          depth={1.1}
          debugColor={'pink'}
        />
        {model.hatchType !== ShipDeckHatchType.Raised ? null : (
          <group
            scale={[
              Math.max(width.num, height.num),
              Math.min(width.num, height.num),
              1,
            ]}
            rotation-y={width.gte(height) ? 0 : -Math.PI / 2.0}>
            {[0, 1, 2, 3].flatMap((x) =>
              [0, 1, 2, 3].flatMap((y) => (
                <FeatureBox3D
                  key={`${x} ${y}`}
                  feature={model}
                  position={[(x + 1.5) / 5.5 - 0.5, 0, (y + 1) / 5 - 0.5]}
                  width={0.15}
                  height={0.1}
                  depth={1.1}
                  debugColor={'pink'}
                />
              ))
            )}
            <FeatureBox3D
              feature={model}
              position={[-0.4, 0, 0.2]}
              width={0.05}
              height={0.3}
              depth={1.1}
              debugColor={'pink'}
            />
            <FeatureBox3D
              feature={model}
              position={[-0.4, 0, -0.2]}
              width={0.05}
              height={0.3}
              depth={1.1}
              debugColor={'pink'}
            />
          </group>
        )}
        {model.hatchType !== ShipDeckHatchType.RaisedWithScuttle ? null : (
          <>
            <FeatureCylinder3D
              feature={model}
              position={[0, 0, 0]}
              radius={raisedOuterWheelRadius}
              height={1.1}
              debugColor={'pink'}
            />
            <FeatureCylinder3D
              feature={model}
              position={[0, 0, 0]}
              radius={raisedInnerWheelRadius}
              height={1.2}
              debugColor={'pink'}
            />
            <FeatureBox3D
              feature={model}
              position={[0, 0, 0]}
              width={2 * raisedInnerWheelRadius}
              height={0.001}
              depth={1.2}
              debugColor={'pink'}
            />
            <FeatureBox3D
              feature={model}
              position={[0, 0, 0]}
              width={0.001}
              height={2 * raisedInnerWheelRadius}
              depth={1.2}
              debugColor={'pink'}
            />
          </>
        )}
        {model.hatchType !== ShipDeckHatchType.RaisedSpring ? null : (
          <>
            <FeatureCylinder3D
              feature={model}
              position={[0, 0, 0]}
              radius={raisedSpringWheelRadius}
              height={1.1}
              debugColor={'pink'}
            />
            <FeatureBox3D
              feature={model}
              position={[0, 0, 0]}
              width={2 * raisedSpringWheelRadius}
              height={0.001}
              depth={1.1}
              debugColor={'pink'}
            />
            <FeatureBox3D
              feature={model}
              position={[0, 0, 0]}
              width={0.001}
              height={2 * raisedSpringWheelRadius}
              depth={1.1}
              debugColor={'pink'}
            />
            <FeatureBox3D
              feature={model}
              position={[
                raisedSpringRectangle1XPos,
                0,
                raisedSpringRectangle1YPos,
              ]}
              width={raisedSpringRectangle1Width}
              height={raisedSpringRectangle1Height}
              depth={1.1}
              debugColor={'pink'}
            />
            <FeatureBox3D
              feature={model}
              position={[
                raisedSpringRectangle2XPos,
                0,
                raisedSpringRectangle2YPos,
              ]}
              width={raisedSpringRectangle2Width}
              height={raisedSpringRectangle2Height}
              depth={1.1}
              debugColor={'pink'}
            />
          </>
        )}
        {model.hatchType !== ShipDeckHatchType.Flush ? null : (
          <>
            <FeatureCylinder3D
              feature={model}
              position={[0, 0, 0]}
              radius={flushCircleRadius}
              height={1.1}
              debugColor={'pink'}
            />
            <FeatureBox3D
              feature={model}
              position={[flushRectangleXPos, 0, flushRectangleYPos]}
              width={flushRectangleWidth}
              height={flushRectangleHeight}
              depth={1.2}
              debugColor={'pink'}
            />
          </>
        )}
        {model.hatchType !== ShipDeckHatchType.Ramped ? null : (
          <FeatureBox3D
            feature={model}
            position={[0, 0, 0]}
            width={width.scale(0.8).num}
            height={height.scale(0.8).num}
            depth={1.1}
            debugColor={'pink'}
          />
        )}
      </SurfaceFeature3D>
    </>
  )
}
